import React, {
  FunctionComponent,
  useEffect,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { TMapOptions, TPoint, TPointBounds, TImageIcon } from 'types/naverMap';
import { TMapTypeId } from 'enum';
import styled from 'styled-components';
import qs from 'qs';
import {
  addWebViewEventListener,
  removeWebViewEventListener,
} from 'utils/ReactNativeMessage';
import './InfoWindow.css';
import { useDispatch } from 'react-redux';
import {
  setSido,
  setSigungu,
  setSigunguList,
} from 'redux/store/branchMapReducer';
import clientAxios from 'api/clientAxios';

export interface INaverMap extends TMapOptions {
  className?: string;
  width: string | number;
  height: string | number;
  mapTypeId?: TMapTypeId;
  onClick?: (e: { position: TPoint }) => void;
  onChangedZoom?: (zoom: number) => void;
  onChangedBounds?: (bounds: TPointBounds) => void;
  onMouseUp?: (bounds: any, string: any) => void;
}

export interface branchInfo {
  branchInfoNo: number;
  branchRegionStr?: string | null;
  branchNameStr: string;
  branchNameSubStr?: string | null;
  branchAddressStr: string;
  branchAssignedDistrictStr: string | null;
  branchTelStr?: string | null;
  branchFaxStr?: string | null;
  emailStr?: string | null;
  representative?: null;
  regNoStr?: null;
  comTradeRegNo?: null;
  center?: {
    latitude: number;
    longitude: number;
  };
  parking?: string | null;
  marker?: any | null;
}

const { naver } = window;
let mapData = {};
let map;
// eslint-disable-next-line import/no-mutable-exports
let BranchMap: FunctionComponent<INaverMap> = (props) => {
  const dispatch = useDispatch();
  const container = useRef(null);
  const [popup, setPopup] = useState(false);
  const { zoom } = props;
  const markerArray: any[] = [];
  const infoWindowArray: any[] = [];

  function closeInfoWindows() {
    infoWindowArray.forEach((item) => {
      if (item.getMap()) {
        item.close();
      }
    });
  }

  useEffect(() => {
    const listener = (e: any) => {
      const { payload, type } = JSON.parse(e.data);
      // zoom out
      const zoomLevel = 7;
      const centerY = 35.60413;
      const centerX = 128.186638;
      if (type === 'showAllBranches') {
        dispatch(setSido('전체'));
        dispatch(setSigungu(''));
        dispatch(setSigunguList([]));
        closeInfoWindows();
        map.morph(new naver.maps.LatLng(centerY, centerX), zoomLevel);
      } else if (type === 'moveMarker') {
        const selectedMarker = markerArray.find((item) => item.id === payload);
        selectedMarker.trigger('click');
        const position = selectedMarker.getPosition();
        map.morph(
          // 인포윈도우 중앙 배치를 위한 경도값 추가
          new naver.maps.LatLng({ x: position.x, y: position.y + 0.007 }),
          14,
        );
      }
    };
    addWebViewEventListener(listener);

    return () => {
      removeWebViewEventListener(listener);
    };
  }, []);

  function getInfoWindowContent(data: branchInfo) {
    const content = `<div class="container">
  <div class="branch-name-container">
    <div class="empty-left"></div>
    <div class="branch-name">${data.branchNameStr}</div>
    <div class="close-container" id="infoWindow_${data.branchInfoNo}">
      <img
        style="width: 19px; height: 19px"
        src="/images/branch/ic_close_default.png"
      />
    </div>
  </div>
  <div class="content-top">
    <div class="nav">
      <img class="nav-icon" src="/images/branch/location_sm.png" />
      <div class="nav-title">위치</div>
    </div>
    <div class="content-row">
      ${data.branchAddressStr}
    </div>
  </div>
  <div class="flex-row">
    <div class="nav">
      <img class="nav-icon" src="/images/branch/parking.png" />
      <div class="nav-title">주차시설</div>
    </div>
    <div class="content-row">${data.parking ? data.parking : '불가능'}</div>
  </div>
  <div class="flex-row">
    <div class="nav">
      <img class="nav-icon" src="/images/branch/tel.png" />
      <div class="nav-title">전화번호</div>
    </div>
    <div class="content-row">${data.branchTelStr}</div>
  </div>
  <div class="flex-row">
    <div class="nav">
      <img class="nav-icon" src="/images/branch/estate.png" />
      <div class="nav-title">중개가능지역</div>
    </div>
    <div class="content-row">${data.branchAssignedDistrictStr}</div>
  </div>
</div>
<div class="anchor"></div>

`;

    return content;
  }

  function createMarker(item: branchInfo) {
    const iconConfig: TImageIcon = {
      url: `/images/branch/branch_marker.png`,
      size: new naver.maps.Size(40, 50),
      scaledSize: new naver.maps.Size(40, 50),
    };

    const position: TPoint = naver.maps.LatLng(
      item?.center?.latitude,
      item?.center?.longitude,
    );

    const marker = new naver.maps.Marker({
      map,
      position,
      icon: iconConfig,
      id: item.branchInfoNo,
    });

    const infoWindow = new naver.maps.InfoWindow({
      content: getInfoWindowContent(item),
      disableAutoPan: true,
      maxWidth: 330,
      backgroundColor: 'transparent',
      borderWidth: 0,
      disableAnchor: true,
      pixelOffset: naver.maps.Point(0, -10),
    });

    marker.addListener('click', () => {
      if (infoWindow.getMap()) {
        infoWindow.close();
      } else {
        infoWindow.open(map, marker);
      }

      const closeButton = document.getElementById(
        `infoWindow_${item.branchInfoNo}`,
      );

      closeButton?.addEventListener('click', () => {
        infoWindow.close();
      });
    });

    markerArray.push(marker);
    infoWindowArray.push(infoWindow);
  }

  const drawMarker = useCallback(async () => {
    try {
      const { data } = await clientAxios.get(`/branch/api/mapList`, {
        params: mapData,
        paramsSerializer: (params) => {
          return qs.stringify(params, { arrayFormat: 'repeat' });
        },
      });

      const branchInfoList: branchInfo[] = data.data;
      branchInfoList.forEach((item) => {
        createMarker(item);
      });
    } catch (error) {
      console.log('error', error);
    }
  }, [mapData]);

  useLayoutEffect(() => {
    if (!naver) {
      console.log('naver map not loaded');
    }

    // to be changed - 강서구 중심좌표
    const zoomLevel = 9;
    const centerY = 37.5474;
    const centerX = 126.836042;

    async function drawMap() {
      map = await new naver.maps.Map(container.current, { zoom });
      await map.morph(new naver.maps.LatLng(centerY, centerX), zoomLevel);
    }

    drawMap();
  }, []);

  useEffect(() => {
    const initListener = map.addListener('init', () => {
      drawMarker();
    });

    const center = map.addListener('idle', () => {
      if (map.zoom <= 6) {
        setPopup(true);
      } else {
        setPopup(false);
      }
    });

    return () => {
      map.removeListener([center, initListener]);
    };
  }, [drawMarker]);

  return (
    <>
      {naver && (
        <NaverMap ref={container}>
          <PopUp pop={popup}>
            <PopupLabel>지도를 확대해주세요.</PopupLabel>
          </PopUp>
        </NaverMap>
      )}
    </>
  );
};

export default BranchMap;

const PopUp = styled.div<{ pop: boolean }>`
  display: ${(props) => (props.pop ? 'block' : 'none')};
  /* width: 200px; */
  border-radius: 40px;
  width: 40%;
  z-index: 1;
  top: 50%;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);
  padding: 16px;
  opacity: 0.5;
  position: absolute;
  background-color: black;
`;

const PopupLabel = styled.div`
  font-family: Pretendard;
  font-style: normal;
  font-weight: bold;
  font-size: 14px;
  line-height: 20px;
  vertical-align: middle;
  text-align: center;
  color: white;
`;

const NaverMap = styled.div`
  width: 100%;
  height: 100%;
`;

BranchMap.defaultProps = {
  center: { x: 33.3572421, y: 126.5322317 },
  mapTypeId: 'NORMAL',
};

BranchMap = styled(BranchMap)`
  width: ${({ width }) => (typeof width === 'number' ? `${width}px` : width)};
  height: ${({ height }) =>
    typeof height === 'number' ? `${height}px` : height};
`;
